import { useEffect, useMemo, useState } from 'react';

import {
  faArrowRight,
  faGear,
} from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { faBullseyeArrow } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@repo/videa-components/src/index';
import classNames from 'classnames';
import { differenceInDays, isBefore, isToday, startOfDay } from 'date-fns';
import { useAtomValue } from 'jotai';

import { DiveBar } from '../../../../app/snackbar/Divebar';
import {
  useGetDailyGoals,
  useUpdateDailyGoals,
} from '../../../../react-query/useDailyGoals';
import { usePreferences } from '../../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../../utils/mixpanel';
import { selectDateAtom, selectedProvidersAtom } from '../../atoms';
import { ViewAllButton } from '../ViewAllButton';

import { CoreFourTppModal } from '../tpp/CoreFourTppModal';
import { DailyGoalRow } from './DailyGoalRow';
import { SetDailyGoalModal } from './SetDailyGoalModal';

interface DailyGoalsProps {
  loading?: boolean;
}

const LOW_HEIGHT_THRESHOLD = 800;

export const DailyGoals = ({ loading = false }: DailyGoalsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLowHeight, setIsLowHeight] = useState(false);
  const [isCoreFourTppModalOpen, setIsCoreFourTppModalOpen] = useState(false);
  const [isSetModalOpen, setIsSetModalOpen] = useState(false);
  const selectedDate = useAtomValue(selectDateAtom);
  const selectedProviders = useAtomValue(selectedProvidersAtom);
  const { trackEvent } = useTrackEvent();
  const { t } = useLingui();
  const updateDailyGoals = useUpdateDailyGoals();
  const { data: prefs } = usePreferences();
  const canEditPastDailyGoals = prefs?.enableEditPastDailyGoals === true;
  const isTppEnabled = prefs?.enableTpp === true;

  const isOneProviderFiltered = selectedProviders.length === 1;

  // Get provider IDs for filtering
  const providerIds = useMemo(() => {
    const ids = selectedProviders
      .map((p) => p.id)
      .filter((id): id is string => id !== undefined);
    return ids.length > 0 ? ids : undefined;
  }, [selectedProviders]);

  // Fetch daily goals data
  const { filteredAndSortedGoals, result } = useGetDailyGoals({
    date: selectedDate,
    providerIds,
  });

  const isLoadingGoals = result.isLoading || loading;

  // Derive unique provider IDs from the daily goals data so we can show
  // the Core Four TPP button even when no provider filter is active but
  // only one provider exists in the goals.
  const uniqueGoalProviderIds = useMemo(() => {
    return new Set(filteredAndSortedGoals.map((g) => g.providerVideaId).filter(Boolean));
  }, [filteredAndSortedGoals]);

  const hasOneDailyGoalsProvider = uniqueGoalProviderIds.size === 1;
  const canShowCoreFourTpp = isOneProviderFiltered || hasOneDailyGoalsProvider;

  const singleProviderId = isOneProviderFiltered
    ? selectedProviders[0]?.id
    : hasOneDailyGoalsProvider
      ? filteredAndSortedGoals[0]?.providerVideaId
      : undefined;

  // Handle window resize for low-height behavior
  useEffect(() => {
    const handleResize = () => {
      setIsLowHeight(window.innerHeight < LOW_HEIGHT_THRESHOLD);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleChangeExpanded = (newIsExpanded: boolean) => {
    setIsExpanded(newIsExpanded);
    trackEvent(mixpanelEvents.userViewAllClick, {
      expanded: newIsExpanded ? 'all' : 'less',
      context: 'DAILY_GOALS',
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
  };

  const handleSaveDailyGoals = (
    updates: { providerId: string; goal: number }[],
  ) => {
    if (!selectedDate || updates.length === 0) return;
    updateDailyGoals.mutate(
      { date: selectedDate, updates },
      { onSuccess: () => setIsSetModalOpen(false) },
    );
  };

  const handleViewCoreFourTpp = () => {
    if (!selectedDate || !singleProviderId) return;
    trackEvent(mixpanelEvents.coreFourTppClickOpen, {
      context: 'TPP',
      date: selectedDate,
      providerId: singleProviderId,
      isToday: isToday(selectedDate),
    });
    setIsCoreFourTppModalOpen(true);
  };

  const baseLimit = isLowHeight ? 2 : 3;
  const canExpand = filteredAndSortedGoals.length > baseLimit;

  // Determine how many items to show
  const displayLimit = isExpanded ? filteredAndSortedGoals.length : baseLimit;
  const goalsToShow = filteredAndSortedGoals.slice(0, displayLimit);
  const isPastDate = selectedDate
    ? isBefore(startOfDay(new Date(selectedDate)), startOfDay(new Date()))
    : false;
  const isEditingDisabledForDate = isPastDate && !canEditPastDailyGoals;

  useEffect(() => {
    if (isEditingDisabledForDate && isSetModalOpen) {
      setIsSetModalOpen(false);
    }
  }, [isEditingDisabledForDate, isSetModalOpen]);

  const isMoreThan7DaysInFuture =
    selectedDate &&
    differenceInDays(
      startOfDay(new Date(selectedDate)),
      startOfDay(new Date()),
    ) > 7;

  if (
    !selectedDate ||
    filteredAndSortedGoals.length === 0 ||
    isMoreThan7DaysInFuture
  ) {
    return null;
  }

  return (
    <>
      <div className="vai-flex vai-flex-col vai-gap-2 vai-mt-4">
        <div className="vai-flex vai-items-center vai-justify-between vai-px-2">
          <div className="vai-flex vai-items-center vai-gap-2">
            <FontAwesomeIcon
              className="vai-flex vai-h-4 vai-items-center vai-justify-center vai-text-zinc-600"
              icon={faBullseyeArrow}
            />
            <h3 className="vai-font-inter vai-text-[12px] vai-font-semibold vai-leading-[14px] vai-text-zinc-600">
              {t`Daily Goals`}
            </h3>
          </div>
          <Tooltip delayDuration={50}>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={classNames('vai-bg-transparent vai-border-none', {
                  'vai-cursor-not-allowed vai-opacity-50':
                    isEditingDisabledForDate,
                  'vai-cursor-pointer hover:vai-opacity-80':
                    !isEditingDisabledForDate,
                })}
                disabled={isEditingDisabledForDate}
                onClick={() => {
                  if (isEditingDisabledForDate) return;
                  trackEvent(mixpanelEvents.dailyGoalsSettingsClick, {
                    context: 'DAILY_GOALS',
                    date: selectedDate,
                  });
                  setIsSetModalOpen(true);
                }}
                aria-label={t`Set daily goals`}
                data-testid="daily-goals-settings-button"
              >
                <FontAwesomeIcon
                  icon={faGear}
                  className="vai-h-4 vai-w-4 vai-text-muted-foreground"
                />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {isEditingDisabledForDate
                ? t`Past daily goals cannot be edited`
                : t`Set daily goals`}
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="vai-flex vai-flex-col">
          {goalsToShow.map((goal) => (
            <DailyGoalRow
              key={`${goal.providerId}-${goal.date}`}
              completedProduction={goal.completedProduction}
              goal={goal.goal}
              providerName={goal.providerName}
              scheduledProduction={
                isPastDate ? goal.completedProduction : goal.scheduledProduction
              }
            />
          ))}
        </div>

        {isTppEnabled && canShowCoreFourTpp && !isLoadingGoals && (
          <Button
            variant="outline"
            size="default"
            className="vai-w-full vai-mt-2 vai-leading-5 vai-text-zinc-900"
            onClick={handleViewCoreFourTpp}
            data-testid="daily-goals-view-core-four-tpp"
          >
            <Trans>View Core Four TPP</Trans>
            <FontAwesomeIcon
              icon={faArrowRight}
              className="vai-text-base vai-font-normal"
            />
          </Button>
        )}

        {canExpand && !isLoadingGoals && (
          <ViewAllButton
            dataTestId="view-all-daily-goals"
            expanded={isExpanded}
            onChange={handleChangeExpanded}
          />
        )}
      </div>

      <SetDailyGoalModal
        open={isSetModalOpen}
        onOpenChange={setIsSetModalOpen}
        date={selectedDate}
        goals={filteredAndSortedGoals}
        onSave={handleSaveDailyGoals}
        isLoading={updateDailyGoals.isLoading}
      />

      {isTppEnabled && (
        <CoreFourTppModal
          open={isCoreFourTppModalOpen}
          onOpenChange={setIsCoreFourTppModalOpen}
          providerName={
            canShowCoreFourTpp
              ? filteredAndSortedGoals[0]?.providerName
              : undefined
          }
          providerId={singleProviderId}
        />
      )}

      <DiveBar
        open={updateDailyGoals.isError}
        message={t`Error saving daily goals. Please try again or contact support.`}
        severity="error"
        closable
        handleClose={updateDailyGoals.reset}
      />
    </>
  );
};
